import { useState, useEffect, useRef } from "react"
import { chatEdit, saveSeason, getUserProfile, getKnownSeasonHash } from "@/lib/api"
import { useWorkoutStore } from "@/stores/workoutStore"
import { WeekCalendar } from "./WeekCalendar"
import { UserCombobox, type UserOption } from "./user-combobox"
import { UserProfileCard } from "./user-profile-card"
import { toast } from "sonner"
import { Send, Save, X, GitCompare } from "lucide-react"
import type { Season } from "@/types/workout"
import { walkSeasonWeeks } from "@/types/workout"
import { useReadOnly } from "@/lib/read-only"
import { DiffViewer } from "./DiffViewer"

type ChatMessage = { role: "user" | "assistant"; content: string }

export function ChatEdits() {
  const { readOnly } = useReadOnly()
  const {
    season,
    setSeason,
    setCurrentUser,
    loadUserSeason,
    isLoading,
    setCurrentWeekIndex,
    currentUser,
  } = useWorkoutStore()

  const [selectedUser, setSelectedUser] = useState<UserOption | null>(() =>
    currentUser
      ? {
          id: currentUser.id,
          first_name: currentUser.first_name,
          last_name: currentUser.last_name,
        }
      : null,
  )

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState("")
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const [selectedUserProfile, setSelectedUserProfile] = useState<Record<
    string,
    unknown
  > | null>(null)

  // Pending save (the AI-proposed season + a snapshot of the season as it was
  // BEFORE the AI ran, so we can show a structured diff in the right rail).
  const [pendingSeasonSave, setPendingSeasonSave] = useState<Season | null>(
    null,
  )
  const [pendingSeasonHash, setPendingSeasonHash] = useState<string | undefined>(
    undefined,
  )
  const [previousSeasonSnapshot, setPreviousSeasonSnapshot] = useState<Season | null>(
    null,
  )
  const [showDiff, setShowDiff] = useState(false)
  const [isSavingPlan, setIsSavingPlan] = useState(false)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isSending])

  // Navigate to first week with future dates (multi-cycle aware)
  useEffect(() => {
    if (!season) return
    const today = new Date().toISOString().split("T")[0]
    const flattened = walkSeasonWeeks(season)
    const firstFutureIndex = flattened.findIndex(({ week }) =>
      Object.keys(week.days).some((date) => date > today),
    )
    setCurrentWeekIndex(
      firstFutureIndex >= 0
        ? firstFutureIndex
        : Math.max(flattened.length - 1, 0),
    )
  }, [season, setCurrentWeekIndex])

  const handleSelectUser = async (user: UserOption | null) => {
    setSelectedUser(user)
    setMessages([])
    setPendingSeasonSave(null)
    setPendingSeasonHash(undefined)
    setSelectedUserProfile(null)

    if (!user) {
      setCurrentUser(null)
      setSeason(null)
      return
    }

    setCurrentUser({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
    })
    await loadUserSeason(user.id)
    getUserProfile(user.id)
      .then(setSelectedUserProfile)
      .catch(() => setSelectedUserProfile(null))
  }

  const handleSend = async () => {
    if (!chatInput.trim() || !selectedUser || isSending) return

    const userMessage = chatInput.trim()
    setChatInput("")
    const prevMessages = messages
    const newMessages: ChatMessage[] = [
      ...messages,
      { role: "user", content: userMessage },
    ]
    setMessages(newMessages)
    setIsSending(true)

    // Snapshot the season state BEFORE the AI runs so the diff viewer has
    // a stable baseline regardless of how the store mutates afterward.
    const baseline = season
    const baseHash = getKnownSeasonHash(selectedUser.id)
    try {
      const result = await chatEdit(selectedUser.id, userMessage, messages)
      setMessages([
        ...newMessages,
        { role: "assistant", content: result.response },
      ])
      setSeason(result.season)
      setPendingSeasonSave(result.season)
      setPendingSeasonHash(baseHash)
      setPreviousSeasonSnapshot(baseline)
      setShowDiff(true)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to process request",
      )
      setMessages(prevMessages)
      setChatInput(userMessage)
    } finally {
      setIsSending(false)
    }
  }

  const handleSavePlan = async () => {
    if (!pendingSeasonSave || !selectedUser) return
    setIsSavingPlan(true)
    try {
      await saveSeason(selectedUser.id, pendingSeasonSave, pendingSeasonHash)
      setPendingSeasonSave(null)
      setPendingSeasonHash(undefined)
      setPreviousSeasonSnapshot(null)
      setShowDiff(false)
      toast.success("Plan saved successfully")
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save plan",
      )
    } finally {
      setIsSavingPlan(false)
    }
  }

  const handleRejectPlan = () => {
    // Roll the calendar back to what the admin saw before the AI ran. If the
    // baseline was null (AI created a season from scratch), still let the
    // admin discard the pending plan.
    setSeason(previousSeasonSnapshot)
    setPendingSeasonSave(null)
    setPendingSeasonHash(undefined)
    setPreviousSeasonSnapshot(null)
    setShowDiff(false)
    toast.info(
      previousSeasonSnapshot
        ? "AI changes rejected — season reverted"
        : "AI plan discarded",
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-card border-b flex items-center gap-4">
          <UserCombobox
            value={selectedUser}
            onSelect={handleSelectUser}
            placeholder="Select a user..."
          />

          {pendingSeasonSave && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-amber-600 font-medium">
                Unsaved AI changes
              </span>
              <button
                onClick={() => setShowDiff((v) => !v)}
                title={showDiff ? "Hide diff" : "Review what the AI changed"}
                className="flex items-center gap-1.5 px-2.5 py-1.5 border border-border text-sm rounded-md hover:bg-accent"
              >
                <GitCompare className="size-3.5" />
                {showDiff ? "Hide diff" : "Review diff"}
              </button>
              <button
                onClick={handleRejectPlan}
                disabled={isSavingPlan}
                title="Discard AI changes and revert"
                className="flex items-center gap-1.5 px-2.5 py-1.5 border border-red-300 text-red-700 bg-red-50 text-sm rounded-md hover:bg-red-100 disabled:opacity-50"
              >
                <X className="size-3.5" />
                Reject
              </button>
              <button
                onClick={handleSavePlan}
                disabled={readOnly || isSavingPlan}
                title={readOnly ? "Read-only mode active" : "Approve and save"}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="size-3.5" />
                {isSavingPlan ? "Saving..." : "Approve & Save"}
              </button>
            </div>
          )}
        </div>

        {selectedUser && selectedUserProfile && (
          <div className="px-4 py-2 border-b">
            <UserProfileCard profile={selectedUserProfile} compact />
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full size-10 border-b-2 border-primary" />
            </div>
          )}
          {!isLoading && season && (
            <div className="px-4 pt-4 space-y-4">
              {showDiff && pendingSeasonSave && (
                <div className="rounded-lg border bg-amber-50/40 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm flex items-center gap-1.5">
                      <GitCompare className="size-4 text-amber-700" />
                      Pending AI Changes
                    </h3>
                    <span className="text-[10px] uppercase tracking-wide text-amber-700/80">
                      Approve & Save to keep · Reject to revert
                    </span>
                  </div>
                  <DiffViewer before={previousSeasonSnapshot} after={pendingSeasonSave} />
                </div>
              )}
              <WeekCalendar />
            </div>
          )}
          {!isLoading && !season && (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              {selectedUser
                ? "No workout plan found for this user"
                : "Select a user to view their workout plan"}
            </div>
          )}
        </div>
      </div>

      <div className="w-[350px] shrink-0 flex flex-col border-l bg-card">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && !isSending && (
            <div className="text-center text-muted-foreground text-sm mt-8">
              {selectedUser
                ? "Ask me to make changes to the workout plan"
                : "Select a user to start editing"}
            </div>
          )}
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                }`}
              >
                {msg.content}
              </div>
            </div>
          ))}
          {isSending && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg px-3 py-2.5">
                <div className="flex gap-1 items-center">
                  <span
                    className="size-2 bg-muted-foreground rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <span
                    className="size-2 bg-muted-foreground rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <span
                    className="size-2 bg-muted-foreground rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t">
          <textarea
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              selectedUser
                ? "Ask to modify the workout plan... (Enter to send)"
                : "Select a user first"
            }
            disabled={readOnly || !selectedUser || isSending}
            rows={3}
            className="w-full px-3 py-2 border rounded-md text-sm resize-none focus:outline-hidden focus:ring-2 focus:ring-primary disabled:opacity-50 bg-background"
          />
          <button
            onClick={handleSend}
            disabled={readOnly || !selectedUser || !chatInput.trim() || isSending}
            className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="size-4" />
            {isSending ? "Thinking..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  )
}
